import { useCallback, useEffect, useState } from "react";
import api, { fmtMXN, fmtDate } from "@/lib/api";
import ReceiptViewer from "@/components/ReceiptViewer";
import { Images, FileText, Search, Loader2 } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;
const PAGE = 60;

const MODULE_COLORS = {
  obra: "var(--cyan)", admin: "#fbbf24", destajos: "var(--magenta)", flotilla: "#a78bfa",
  prestamos: "#fb923c", bitacora: "var(--green)", taller: "#f87171",
};

export default function FilesGallery() {
  const [filters, setFilters] = useState({ start: "", end: "", module: "", q: "" });
  const [data, setData] = useState({ items: [], total: 0, modules: {} });
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [viewer, setViewer] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { skip: page * PAGE, limit: PAGE };
      if (filters.start) params.start = filters.start;
      if (filters.end) params.end = filters.end;
      if (filters.module) params.module = filters.module;
      if (filters.q.trim()) params.q = filters.q.trim();
      const { data } = await api.get("/files-gallery", { params });
      setData(data);
    } catch {
      toast.error("No se pudo cargar la galería");
    } finally {
      setLoading(false);
    }
  }, [page, filters]);
  useEffect(() => { setPage(0); /* eslint-disable-next-line */ }, [filters.start, filters.end, filters.module, filters.q]);
  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const totalPages = Math.max(1, Math.ceil(data.total / PAGE));
  const isImage = (ct) => (ct || "").startsWith("image/");

  return (
    <div className="space-y-5" data-testid="files-gallery-page">
      <div>
        <div className="label-mono">// GALERÍA CENTRAL DE ARCHIVOS</div>
        <h1 className="font-display font-bold text-slate-100" style={{ fontSize: "1.75rem" }}>Tickets y fotos</h1>
      </div>

      <div className="panel p-3 flex flex-wrap items-end gap-3">
        <div>
          <label className="label-mono block mb-1">Desde</label>
          <input type="date" data-testid="gal-start" value={filters.start} onChange={(e) => setFilters({ ...filters, start: e.target.value })} className="input-cyber px-2.5 py-1.5 text-xs" />
        </div>
        <div>
          <label className="label-mono block mb-1">Hasta</label>
          <input type="date" data-testid="gal-end" value={filters.end} onChange={(e) => setFilters({ ...filters, end: e.target.value })} className="input-cyber px-2.5 py-1.5 text-xs" />
        </div>
        <div>
          <label className="label-mono block mb-1">Módulo</label>
          <select data-testid="gal-module" value={filters.module} onChange={(e) => setFilters({ ...filters, module: e.target.value })} className="input-cyber px-2.5 py-1.5 text-xs">
            <option value="">Todos</option>
            {Object.entries(data.modules || {}).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </div>
        <div className="flex-1 min-w-[180px]">
          <label className="label-mono block mb-1">Buscar</label>
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500" />
            <input data-testid="gal-search" value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })}
              placeholder="Descripción, placas, trabajador…" className="input-cyber pl-8 pr-3 py-1.5 text-xs w-full" />
          </div>
        </div>
        <div className="label-mono text-[10px]" data-testid="gal-total">{data.total} archivo{data.total === 1 ? "" : "s"}</div>
      </div>

      {loading ? (
        <div className="panel p-10 text-center text-slate-400"><Loader2 className="w-5 h-5 animate-spin inline mr-2" />Cargando…</div>
      ) : data.items.length === 0 ? (
        <div className="panel p-12 text-center text-slate-500" data-testid="gal-empty">
          <Images className="w-12 h-12 mx-auto mb-3 text-slate-600" />
          Sin archivos con esos filtros.
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
          {data.items.map((it, idx) => (
            <button key={`${it.file_id}-${idx}`} data-testid={`gal-item-${it.file_id}`}
              onClick={() => setViewer(it)}
              className="panel overflow-hidden text-left transition-transform hover:-translate-y-0.5">
              <div className="h-28 flex items-center justify-center overflow-hidden" style={{ background: "var(--bg-2)" }}>
                {isImage(it.content_type) ? (
                  <img src={`${API}/api/files/${it.file_id}`} alt={it.label || it.filename} loading="lazy" className="w-full h-full object-cover" />
                ) : (
                  <FileText className="w-9 h-9 text-slate-500" />
                )}
              </div>
              <div className="p-2">
                <div className="flex items-center justify-between gap-1">
                  <span className="text-[9px] font-mono uppercase tracking-widest truncate" style={{ color: MODULE_COLORS[it.module] || "var(--cyan)" }}>
                    {(data.modules || {})[it.module] || it.module}
                  </span>
                  {it.amount != null && <span className="text-[9px] font-mono text-slate-400 shrink-0">{fmtMXN(it.amount)}</span>}
                </div>
                <div className="text-[11px] text-slate-200 truncate mt-0.5">{it.label || it.filename || "—"}</div>
                <div className="text-[9px] font-mono text-slate-500">{it.date ? fmtDate(it.date) : "—"}</div>
              </div>
            </button>
          ))}
        </div>
      )}

      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-3 text-xs">
          <button disabled={page === 0} onClick={() => setPage(page - 1)} data-testid="gal-prev" className="btn-ghost px-3 py-1.5 rounded-sm uppercase tracking-widest disabled:opacity-30">← Anterior</button>
          <span className="font-mono text-slate-400">{page + 1} / {totalPages}</span>
          <button disabled={page + 1 >= totalPages} onClick={() => setPage(page + 1)} data-testid="gal-next" className="btn-ghost px-3 py-1.5 rounded-sm uppercase tracking-widest disabled:opacity-30">Siguiente →</button>
        </div>
      )}

      {viewer && <ReceiptViewer fileId={viewer.file_id} title={viewer.label || viewer.filename} onClose={() => setViewer(null)} />}
    </div>
  );
}
